import * as React from 'react'
import { useEffect } from 'react'
import { connect } from 'react-redux'
import { noop } from 'lodash'
import * as actions from '../../../actions'
import { getStore } from '../../../initializers/redux'
import { reconfigureApi, getToken } from '../../../utilities/configureApi'
import routes from '../../../routes'

import Logo from '@doordash/component-logo'
import { Icon } from '@doordash/design-language'
import NavBar from '@doordash/component-navbar'
import LayerManager from '@doordash/component-layer-manager'
import { RiskProvider } from '@doordash/component-risk'

import { API_PROXY, DASHER_ACTIVATION_HELP } from '../../../constants'
import ManageAccount from '../../organisms/manage-account'
import LoadingBox from '../../atoms/loading-box'
import Page from '../../molecules/page'
import PageRoot from '../../molecules/page-root'
import StickyTop from '../../atoms/sticky-top'

import { requestCredentials, loadUrl } from '../../../webview'
import { DasherCredentials, OsTypes } from '../../../webview/types'
import { isWebview, detectWebviewOs } from '../../../utilities/userAgent'

import i18n from '../../../i18n'

export const ANDROID_CANCEL_DEEPLINK = `dddriver://privacy/cancel`
export const IOS_CANCEL_DEEPLINK = `comdoordashdriver://privacy/cancel`

declare const window

window.requestDasherCredentials = ({
  token,
  dasherId,
  deviceId,
}: DasherCredentials) => {
  const store = getStore()
  window.webview.token = token
  window.webview.deviceId = deviceId
  reconfigureApi(token)
  store.dispatch(actions.setDasherId(dasherId))
}

type PrivacyPortalProps = {
  dasherId?: string
  setDasherId?: (dasherId: string) => void
  loadDasherId?: () => Promise<void>
}

export const PrivacyPortal: React.FC<PrivacyPortalProps> = ({
  dasherId,
  setDasherId,
  loadDasherId,
}) => {
  const token = window.webview.token || getToken() || null
  const baseUrl = window.view.API_URL ? new URL(window.view.API_URL) : null

  useEffect(() => {
    if (isWebview()) {
      requestCredentials(data => {
        setDasherId(data.dasherId)
      })
    } else {
      loadDasherId()
    }
  }, [loadDasherId, setDasherId])

  if (!token || !dasherId) {
    if (isWebview()) {
      return <LoadingBox />
    } else if (!token) {
      window.location.assign(routes.privacyLogin.path)
    }
  }
  const os = detectWebviewOs()

  return (
    <RiskProvider
      authToken={token}
      language={i18n.getCurrentLanguage()}
      proxyOverride={{
        production: {
          baseUrl: baseUrl ? `${baseUrl.protocol}//${baseUrl.host}` : '',
          apiProxy: API_PROXY,
        },
      }}
    >
      <LayerManager>
        <StickyTop>
          <NavBar
            navigationType={
              isWebview()
                ? NavBar.NavigationTypes.Back
                : NavBar.NavigationTypes.None
            }
            onNavigationClick={() =>
              window.location.assign(
                os === OsTypes.iOS
                  ? IOS_CANCEL_DEEPLINK
                  : ANDROID_CANCEL_DEEPLINK
              )
            }
            title={
              <div style={{ margin: '-2px 0 0', maxHeight: 24 }}>
                <Logo />
              </div>
            }
            onOverflowClick={noop}
            actions={
              isWebview()
                ? [
                    {
                      iconType: Icon.Types.HelpCircleLine,
                      accessibilityLabel: 'help',
                      onClick: () => {
                        loadUrl(DASHER_ACTIVATION_HELP)
                      },
                    },
                  ]
                : null
            }
          />
        </StickyTop>
        <Page>
          <PageRoot>
            <ManageAccount />
          </PageRoot>
        </Page>
      </LayerManager>
    </RiskProvider>
  )
}

PrivacyPortal.defaultProps = {
  setDasherId: () => {}, // noop
  loadDasherId: () => Promise.resolve(), // noop
}

const mapStateToProps = state => ({
  dasherId: state.dasherId,
  experiments: state.experiments,
})

const mapDispatchToProps = {
  loadApplicationWebview: actions.loadApplicationWebview,
  setDasherId: actions.setDasherId,
  loadDasherId: actions.loadDasherId,
}

export default connect(mapStateToProps, mapDispatchToProps)(PrivacyPortal)
